/* ============================================================
   SESAAT STUDIO v3.9 — KOMPONEN KONGSI
   ============================================================
   Bina sekali, guna di mana-mana.

   Peraturan:
   - JANGAN tulis kod warna dalam komponen. Guna token --s-*.
   - Ikon dihantar masuk sebagai prop (`icon`) — komponen ini
     tidak terikat pada mana-mana pustaka ikon.
   ============================================================ */

import './ui.css';
import { klikBiasa } from '../utils/klikBiasa';
import { useLang, SENARAI_BAHASA } from '../i18n';

const cx = (...parts) => parts.filter(Boolean).join(' ');

/* ---------- Badge -------------------------------------------------
   tone: neutral | brand | ok | warn | stop                      */
export function Badge({ tone = 'neutral', children, className, ...rest }) {
    return (
        <span className={cx('s-badge', `s-badge--${tone}`, className)} {...rest}>{children}</span>
    );
}

/* ---------- Butang ------------------------------------------------
   variant: primary | ghost | quiet                              */
export function Btn({ variant = 'primary', size, icon, children, className, type = 'button', ...rest }) {
    return (
        <button
            type={type}
            className={cx('s-btn', `s-btn--${variant}`, size === 'sm' && 's-btn--sm', className)}
            {...rest}
        >
            {icon}
            {children}
        </button>
    );
}

export function IconBtn({ icon, label, className, ...rest }) {
    return (
        <button type="button" aria-label={label} title={label} className={cx('s-iconbtn', className)} {...rest}>
            {icon}
        </button>
    );
}

/* ---------- Kad ---------------------------------------------------- */
export function Card({ hover = false, children, className, ...rest }) {
    return <div className={cx('s-card', hover && 's-card--hover', className)} {...rest}>{children}</div>;
}
export function CardBody({ children, className, ...rest }) {
    return <div className={cx('s-card__body', className)} {...rest}>{children}</div>;
}
export function CardFoot({ children, className, ...rest }) {
    return <div className={cx('s-card__foot', className)} {...rest}>{children}</div>;
}

/* ---------- Kepala halaman ----------------------------------------
   Tajuk kiri, tindakan kanan atas. Sentiasa.

   sisiTajuk — kawalan kecil yang duduk BERSEBELAHAN tajuk (cth pemilih
   group Workspace). Ia dirender di LUAR tajuk, bukan di dalamnya:
   tajuk memakai muka taip besar, tebal 600 dan jarak huruf tersendiri,
   dan semua itu diwarisi oleh apa sahaja yang diletak di dalamnya —
   butang kecil di situ keluar lebih tebal dan berbentuk huruf tajuk
   berbanding butang yang sama di tempat lain. Pilihan sepenuhnya:
   tanpa ia kepala halaman kekal seperti sebelum ini.            */
/* suisBahasa — pil EN/BM di hujung kanan baris tajuk. HIDUP secara
   lalai kerana destinasinya ialah "ada pada setiap halaman".

   ⛔ Matikannya (`suisBahasa={false}`) pada halaman yang ayatnya
      BELUM masuk kamus. Suis pada halaman begitu adalah kawalan
      palsu: pelanggan menekan EN, badan halaman langsung tidak
      berubah, dan dia menyangka app rosak.

      Ia terpakai juga kepada halaman yang kepalanya SUDAH dua bahasa
      tetapi badan/anaknya belum — separuh halaman bertukar, separuh
      lagi tidak, dan itu nampak lebih rosak daripada tiada suis.

      Keadaan 2026-09-23 (trace penuh): SEMUA halaman pelanggan v3.9
      ada pil. `suisBahasa={false}` kini hanya pada BoostAdminView
      (admin, English). Halaman yang TIDAK memakai PageHeader memasang
      <SuisBahasa /> sendiri sebagai elemen terakhir kanan atas —
      SettingsV39View · NotesV39View · MarketplaceProductV39View.
      Halaman baharu dengan kepala sendiri → WAJIB pasang pil yang sama. */
export function PageHeader({ title, subtitle, actions, sisiTajuk, suisBahasa = true, className }) {
    return (
        <div
            className={className}
            style={{
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                gap: 20, flexWrap: 'wrap', marginBottom: 22,
            }}
        >
            <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <h1 className="s-title" style={{ marginBottom: subtitle ? 4 : 0 }}>{title}</h1>
                    {sisiTajuk}
                </div>
                {subtitle && <p className="s-subtitle">{subtitle}</p>}
            </div>
            {/* Tindakan dibenarkan BERPATAH. Halaman dengan bar alat penuh
                (butang + penapis + carian) tidak muat pada satu baris di skrin
                sempit; tanpa `flexWrap` ia terkeluar ke tepi dan menolak seluruh
                halaman menatal ke sisi. Halaman dengan satu-dua butang kecil
                tidak terjejas — ia tidak pernah mencapai titik berpatah. */}
            {(actions || suisBahasa) && (
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                    gap: 8, flexWrap: 'wrap', minWidth: 0,
                }}>
                    {actions}
                    {/* Sentiasa ELEMEN TERAKHIR — pelanggan mencari suis bahasa
                        pada kedudukan yang sama di setiap halaman, jadi ia tidak
                        boleh berpindah-randah mengikut bilangan butang halaman. */}
                    {suisBahasa && <SuisBahasa />}
                </div>
            )}
        </div>
    );
}

/* ---------- Segmented ---------------------------------------------
   options: [{ value, label, disabled, href }]
   `href` PILIHAN: beri ia dan pilihan itu jadi <a> beralamat sebenar,
   supaya klik kanan / Ctrl+klik boleh buka tab baharu (peraturan sama
   seperti baris menu sidebar). Tanpa `href` ia kekal <button> persis
   seperti dahulu — jadi ke-16 pemanggil sedia ada tidak berubah.    */
export function Segmented({ options = [], value, onChange, className }) {
    return (
        <div className={cx('s-seg', className)} role="group">
            {options.map((o) => {
                const sifat = {
                    key: o.value,
                    'aria-pressed': value === o.value,
                    className: cx('s-seg__item', value === o.value && 's-seg__item--on'),
                    onClick: (e) => {
                        if (o.disabled) { e.preventDefault(); return; }
                        /* Pautan: pintas klik kiri kosong SAHAJA; Ctrl/Cmd/Shift
                           dan klik tengah diserahkan kepada browser. */
                        if (o.href) { if (!klikBiasa(e)) return; e.preventDefault(); }
                        if (onChange) onChange(o.value);
                    },
                };
                return o.href
                    ? <a {...sifat} href={o.href}>{o.label}</a>
                    : <button {...sifat} type="button" disabled={o.disabled}>{o.label}</button>;
            })}
        </div>
    );
}

/* ---------- Suis bahasa (EN / BM) ---------------------------------
   Satu bahasa untuk SELURUH app — suis ini menulis tetapan global
   yang sama seperti Settings → Paparan (keputusan user 2026-08-16).
   Ia diletak pada kepala halaman semata-mata supaya pelanggan boleh
   menukar di tempat dia berada, tanpa masuk Settings dahulu; ia
   BUKAN tetapan per-halaman.

   Label pendek ("EN"/"BM") dan nama penuh diambil daripada
   SENARAI_BAHASA — sumber yang SAMA dengan pemilih di Settings,
   supaya dua tempat itu tidak pernah menyebut bahasa yang sama
   dengan nama berbeza. Bukan bendera: bendera mewakili negara,
   bukan bahasa, dan v3.9 memang monokrom tanpa emoji. Nama penuh
   duduk pada tooltip supaya pelanggan yang tersalah tekan masih
   tahu jalan pulang.                                             */
export function SuisBahasa({ className }) {
    const { lang, setLang } = useLang();
    return (
        <Segmented
            className={className}
            value={lang}
            onChange={setLang}
            options={SENARAI_BAHASA.map((b) => ({
                value: b.id,
                label: <span title={b.label}>{b.kod}</span>,
            }))}
        />
    );
}

/* ---------- Baris menu sidebar --------------------------------------
   `mini` = rel menu sedang kuncup: ikon sahaja, nama disorok.

   Nama TIDAK dibuang daripada DOM — ia disorok dengan kelas `s-sr`
   supaya pembaca skrin masih membacakan nama menu. Kalau ia dibuang
   terus, butang jadi butang kosong tanpa nama.

   ⭐ `href` = alamat halaman baris ini. Beri ia pada SETIAP baris yang
      membawa pengguna ke halaman lain — barulah browser melayannya
      sebagai pautan sebenar: klik tengah, Ctrl/Cmd+klik dan menu
      klik-kanan "Open link in new tab" semuanya berfungsi. Tanpa `href`
      ia cuma butang, dan Chrome langsung tidak menawarkan pilihan itu
      (inilah aduan pengguna 2026-08-16).
      Klik kiri biasa kekal navigasi SPA — `onClick` tetap dipanggil dan
      muat semula halaman penuh dihalang. Baris yang BUKAN navigasi
      (kembang/kuncup submenu, log keluar) sengaja tiada `href`.      */
export function NavRow({ active = false, mini = false, icon, children, className, href, onClick, ...rest }) {
    const isi = (
        <>
            {icon && <span className="s-navrow__icon">{icon}</span>}
            <span
                className={mini ? 's-sr' : undefined}
                style={mini ? undefined : { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
                {children}
            </span>
        </>
    );
    const kelas = cx('s-navrow', active && 's-navrow--on', mini && 's-navrow--mini', className);

    if (href) {
        return (
            <a
                href={href}
                className={kelas}
                onClick={(e) => {
                    /* Serahkan kepada browser bila pengguna memang mahu tab/tetingkap
                       lain — kalau tidak, tab baharu akan lahir kosong. */
                    if (!klikBiasa(e)) return;
                    e.preventDefault();
                    onClick?.(e);
                }}
                {...rest}
            >
                {isi}
            </a>
        );
    }

    return (
        <button type="button" className={kelas} onClick={onClick} {...rest}>
            {isi}
        </button>
    );
}

/* ---------- Medan carian ---------------------------------------------
   trailing — elemen kecil di hujung kanan medan (cth butang kosongkan
   carian). Pilihan sepenuhnya: tanpa ia medan kekal betul-betul seperti
   sebelum ini, jadi halaman sedia ada tidak terjejas.              */
export function Field({ icon, trailing, className, style, ...rest }) {
    return (
        <label className={cx('s-field', className)} style={style}>
            {icon}
            <input {...rest} />
            {trailing}
        </label>
    );
}

/* ---------- Petak angka ----------------------------------------------
   Label kecil di atas, angka besar di bawah. Guna untuk ringkasan
   (jumlah jualan, komisen, kuota). Angka WAJIB dibundarkan oleh
   pemanggil — komponen ini tidak menyentuh nilai.                  */
export function StatTile({ label, value, hint, icon }) {
    return (
        <div className="s-stat">
            <div className="s-stat__top">
                <span className="s-stat__label">{label}</span>
                {icon && <span className="s-stat__icon">{icon}</span>}
            </div>
            <div className="s-stat__value">{value}</div>
            {hint && <span className="s-dim">{hint}</span>}
        </div>
    );
}

/* ---------- Senarai baris ---------------------------------------------
   Bekas untuk baris rata yang dipisah garis nipis — bentuk asas
   senarai v3.9 (produk, pesanan, rujukan). Bukan kad bertindan.  */
export function List({ children, className, ...rest }) {
    return <div className={cx('s-list', className)} {...rest}>{children}</div>;
}
/* `href` — beri alamat dan baris ini menjadi <a> dan bukan <div>.

   Peraturan yang sama seperti NavRow: baris yang membawa pengguna ke
   halaman lain WAJIB pautan sebenar, kalau tidak Chrome tidak menawarkan
   "Open link in new tab" dan Ctrl+klik / klik tengah mati. Pemanggil
   memintas klik kiri biasa dengan `klikBiasa` supaya navigasi SPA kekal. */
export function Row({ on = false, href, className, ...rest }) {
    const Tag = href ? 'a' : 'div';
    return <Tag href={href} className={cx('s-list__row', on && 's-list__row--on', className)} {...rest} />;
}

/* ---------- Panel kandungan -------------------------------------------
   Bekas besar yang membungkus isi SETIAP halaman v3.9 — bentuk yang
   diukur daripada cal.com: jurang 10px dari tepi, panel #141414 di atas
   latar halaman #111111, garis rambut keliling, bucu 16px.

   ⚠️ Skrol berlaku DI DALAM panel, bukan pada halaman. Itu yang
      mengekalkan jurang 10px kelihatan sepanjang masa semasa menatal.

   Satu definisi sahaja, sengaja. Bekas ini dipakai oleh DUA tempat —
   StudioShell (halaman prototaip -v3.9) dan halaman v3.9 yang sudah
   dipasang pada app sebenar. Kalau nilainya disalin ke tempat kedua,
   dua bekas itu akan terpesong dan halaman hidup tidak lagi sama rupa
   dengan prototaipnya.

   berlatar — cat latar halaman di belakang panel. WAJIB dalam app
     sebenar: <main> lama berlatar terang, dan jurang 10px itu akan
     menampakkan jalur putih di keliling panel tanpa ini. Dalam
     StudioShell latar sudah dicat oleh grid di luar, jadi tidak perlu.
                                                                      */
export function ContentPanel({ berlatar = false, children }) {
    return (
        <div style={{
            minWidth: 0, height: '100%', padding: 10, overflow: 'hidden',
            background: berlatar ? 'var(--s-ground)' : undefined,
        }}>
            <div style={{
                height: '100%', overflowY: 'auto',
                background: 'var(--s-panel)',
                border: '1px solid var(--s-line)',
                borderRadius: 'var(--s-r-lg)',
            }}>
                {children}
            </div>
        </div>
    );
}

/* ---------- Keadaan kosong -------------------------------------------- */
export function EmptyState({ title, children, action, className }) {
    return (
        <div className={cx('s-empty', className)}>
            <strong className="s-empty__title">{title}</strong>
            {children && <span className="s-empty__body">{children}</span>}
            {action && <div style={{ marginTop: 16 }}>{action}</div>}
        </div>
    );
}
